import { restApi } from 'shared/api/rest-api';
import { getBrowserTimeZone } from 'shared/utils/browser-time-zone';

/**
 * Рендер шаблона media-manager на бэкенде: `POST /api/render-template` сам собирает
 * контекст из своей базы, зовёт сервис рендера и отдаёт готовый PNG.
 *
 * Запрос — **`multipart/form-data`**: фоны с 10.09.2026 загружаются файлами, а не
 * ссылками (`MediaManagerTemplateController::renderTemplate`, аннотация
 * `@OA\MediaType(mediaType="multipart/form-data")`). Скаляры едут отдельными полями
 * формы — именно так они перечислены в схеме аннотации.
 */
export interface RenderTemplateArgs {
  templateId: number;
  /**
   * IANA-зона (`Europe/Moscow`), в которой печатать дату и время матча. Бэкенд требует
   * её с 11.09.2026 в любом режиме контекста и отвечает `400`, если зоны нет или её нет
   * в `DateTimeZone::listIdentifiers()`. Не задана — уходит зона браузера.
   */
  timezone?: string;
  matchId?: number;
  matchIds?: number[];
  championshipId?: number;
  stageId?: number;
  groupId?: number;
  templateSubtypes?: string[];
  /**
   * Загруженные картинки фонов — уезжают файлами в поле `backgrounds`, позиционно по
   * регионам layout-блоков.
   *
   * Байтами, а не ссылкой, потому что до внешних хостов из контейнера рендера не
   * достучаться: URL-фон отвечал `500 Render error`.
   */
  backgrounds?: Blob[];
  /** Старое поведение — фоны ссылками. Бэкенд переименовал прежнее поле в `backgroundUrls`. */
  backgroundUrls?: string[];
}

/**
 * Скаляр в форму — строкой: `FormData` всё равно приводит значение к строке, но явное
 * приведение оставляет в одном месте решение «что именно отправляем».
 */
function appendScalar(form: FormData, name: string, value: number | undefined): void {
  if (value === undefined) return;

  form.append(name, String(value));
}

/**
 * Массив — повторяющимся полем с `[]` в имени: только так php соберёт из плоской формы
 * массив (`$request->request->get('matchIds')`, `$request->files->get('backgrounds')`).
 * Пустой массив не отправляем вовсе — см. `buildRenderTemplateFormData`.
 */
function appendArray(form: FormData, name: string, values: readonly string[]): void {
  values.forEach((value) => form.append(`${name}[]`, value));
}

/**
 * Тело собирается строго под один режим контекста: на бэкенде ветки
 * `contextByMatchId` → `contextByMatchIds` → `contextByChampionshipId` → `contextByStageId`
 * идут подряд **без `else`**, поэтому лишний ключ молча меняет выбранный режим.
 * Ключи со значением `undefined` и пустые массивы не отправляем вовсе.
 */
export function buildRenderTemplateFormData(args: RenderTemplateArgs): FormData {
  const form = new FormData();

  appendScalar(form, 'templateId', args.templateId);
  // Обязательна при любом режиме, хотя время из неё сейчас читают только матчевые.
  form.append('timezone', args.timezone ?? getBrowserTimeZone());

  if (args.matchId !== undefined) {
    appendScalar(form, 'matchId', args.matchId);
  } else if (args.matchIds?.length) {
    appendArray(form, 'matchIds', args.matchIds.map(String));
  } else if (args.championshipId !== undefined) {
    appendScalar(form, 'championshipId', args.championshipId);
  } else if (args.stageId !== undefined) {
    appendScalar(form, 'stageId', args.stageId);
    appendScalar(form, 'groupId', args.groupId);
  }

  if (args.templateSubtypes?.length) {
    appendArray(form, 'templateSubtypes', args.templateSubtypes);
  }

  if (args.backgroundUrls?.length) {
    appendArray(form, 'backgroundUrls', args.backgroundUrls);
  }

  // Имя файла обязательно: без него браузер шлёт часть как обычное поле, и php кладёт
  // её в `$request->request`, а не в `$request->files`.
  args.backgrounds?.forEach((background, index) => {
    form.append('backgrounds[]', background, `background-${index + 1}.${extensionOf(background)}`);
  });

  return form;
}

/** Расширение по mime — только для имени части; на разбор оно не влияет. */
function extensionOf(blob: Blob): string {
  return blob.type === 'image/png' ? 'png' : 'jpg';
}

const renderTemplateApi = restApi.injectEndpoints({
  endpoints: (build) => ({
    renderTemplate: build.mutation<Blob, RenderTemplateArgs>({
      query: (args) => ({
        url: '/render-template',
        method: 'POST',
        // `Content-Type` не задаём: axios сам ставит `multipart/form-data` вместе с
        // boundary, а свой заголовок затёр бы boundary и сломал разбор на бэкенде.
        data: buildRenderTemplateFormData(args),
        // ответ — image/png; при 4xx/5xx тело ошибки тоже придёт блобом,
        // поэтому потребитель различает ошибки по status
        responseType: 'blob',
      }),
    }),
  }),
  overrideExisting: false,
});

export const { useRenderTemplateMutation } = renderTemplateApi;

export default renderTemplateApi;
